package org.zq.astrock_ai.network

import org.zq.astrock_ai.util.formatDecimal

import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.statement.bodyAsText
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

enum class StockNewsCategory {
    NEWS,
    CAPITAL,
    ANNOUNCEMENT,
    FINANCIAL,
    RESEARCH,
}

data class StockNewsEntry(
    val publishedAt: String,
    val category: StockNewsCategory,
    val title: String,
    val source: String,
    val url: String,
)

/**
 * 客户端直连东方财富，获取当前股票的最新资讯和公告。
 * 新闻与公告分别取最新 20 条，再按发布时间倒序合并。
 */
class EastMoneyNewsClient(
    private val client: HttpClient = createMarketHttpClient(),
) {
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    suspend fun fetchLatest(code: String): List<StockNewsEntry> = coroutineScope {
        // 使用股票代码检索可避免同名公司和中文参数编码差异。
        val loaders = listOf<suspend () -> List<StockNewsEntry>>(
            { fetchNews(code) },
            { fetchAnnouncements(code) },
            { fetchFundFlow(code) },
            { fetchFinancials(code) },
            { fetchResearchReports(code) },
        )
        val results = loaders.map { loader ->
            async { runCatching { loader() } }
        }.awaitAll()
        if (results.all { it.isFailure }) {
            error("新闻、公告、资金、财务和研报接口均不可用")
        }
        results
            .flatMap { it.getOrDefault(emptyList()) }
            .distinctBy { "${it.publishedAt}|${it.title}" }
            .sortedByDescending { it.publishedAt }
            .take(100)
    }

    private suspend fun fetchNews(keyword: String): List<StockNewsEntry> {
        val requestJson =
            """{"uid":"","keyword":"${keyword.escapeJson()}","type":["cmsArticleWebOld"],""" +
                """"client":"web","clientType":"web","clientVersion":"curr","param":""" +
                """{"cmsArticleWebOld":{"searchScope":"default","sort":"default","pageIndex":1,""" +
                """"pageSize":20,"preTag":"","postTag":""}}}"""
        val text = client.get(NEWS_URL) {
            parameter("cb", "jQuery")
            parameter("param", requestJson)
            header("User-Agent", UA)
            header("Referer", "https://so.eastmoney.com/")
        }.bodyAsText()
        val body = json.decodeFromString<NewsEnvelope>(unwrapJsonp(text))
        return body.result?.articles.orEmpty().mapNotNull { article ->
            val title = article.title.orEmpty().stripHtml().trim()
            val date = article.date.orEmpty().trim()
            if (title.isBlank() || date.isBlank()) return@mapNotNull null
            StockNewsEntry(
                publishedAt = date,
                category = classifyNews(title),
                title = title,
                source = article.mediaName.orEmpty().ifBlank { "东方财富" },
                url = article.url.orEmpty(),
            )
        }
    }

    private suspend fun fetchAnnouncements(code: String): List<StockNewsEntry> {
        val text = client.get(ANNOUNCEMENT_URL) {
            parameter("sr", "-1")
            parameter("page_size", 20)
            parameter("page_index", 1)
            parameter("ann_type", "A")
            parameter("client_source", "web")
            parameter("stock_list", code)
            parameter("f_node", 0)
            parameter("s_node", 0)
            header("User-Agent", UA)
            header("Referer", "https://data.eastmoney.com/")
        }.bodyAsText()
        val body = json.decodeFromString<AnnouncementEnvelope>(unwrapJsonp(text))
        return body.data?.items.orEmpty().mapNotNull { announcement ->
            val title = (announcement.titleCh ?: announcement.title).orEmpty().stripHtml().trim()
            val date = (announcement.displayTime ?: announcement.noticeDate).orEmpty().trim()
            if (title.isBlank() || date.isBlank()) return@mapNotNull null
            StockNewsEntry(
                publishedAt = date,
                category = classifyAnnouncement(title),
                title = title,
                source = "公司公告",
                url = announcement.artCode
                    ?.takeIf { it.isNotBlank() }
                    ?.let { "https://data.eastmoney.com/notices/detail/$code/$it.html" }
                    .orEmpty(),
            )
        }
    }

    private suspend fun fetchFundFlow(code: String): List<StockNewsEntry> {
        val text = client.get(FUND_FLOW_URL) {
            parameter("secid", org.zq.astrock_ai.api.StockCodes.toSecId(code))
            parameter("klt", 101)
            parameter("lmt", 5)
            parameter("fields1", "f1,f2,f3,f7")
            parameter("fields2", "f51,f52,f53,f54,f55,f56,f57")
            header("User-Agent", UA)
        }.bodyAsText()
        val body = json.decodeFromString<FundFlowEnvelope>(text)
        return body.data?.klines.orEmpty().mapNotNull { line ->
            val parts = line.split(",")
            val date = parts.getOrNull(0).orEmpty()
            val mainNet = parts.getOrNull(1)?.toDoubleOrNull() ?: return@mapNotNull null
            val mainRatio = parts.getOrNull(6)?.toDoubleOrNull()
            val direction = if (mainNet >= 0) "净流入" else "净流出"
            val ratioText = mainRatio?.let { "，占比 ${formatNumber(it)}%" }.orEmpty()
            StockNewsEntry(
                publishedAt = "$date 15:00:00",
                category = StockNewsCategory.CAPITAL,
                title = "主力资金$direction ${formatMoney(kotlin.math.abs(mainNet))}$ratioText",
                source = "东方财富资金流向",
                url = "https://data.eastmoney.com/zjlx/$code.html",
            )
        }
    }

    private suspend fun fetchFinancials(code: String): List<StockNewsEntry> {
        val text = client.get(FINANCIAL_URL) {
            parameter("reportName", "RPT_LICO_FN_CPD")
            parameter("columns", "ALL")
            parameter("filter", "(SECURITY_CODE=\"$code\")")
            parameter("pageNumber", 1)
            parameter("pageSize", 4)
            parameter("sortColumns", "REPORTDATE")
            parameter("sortTypes", -1)
            parameter("source", "WEB")
            parameter("client", "WEB")
            header("User-Agent", UA)
            header("Referer", "https://data.eastmoney.com/")
        }.bodyAsText()
        val body = json.decodeFromString<FinancialEnvelope>(text)
        return body.result?.data.orEmpty().mapNotNull { item ->
            val date = (item.noticeDate ?: item.reportDate).orEmpty()
            if (date.isBlank()) return@mapNotNull null
            val revenue = item.revenue?.let(::formatMoney) ?: "--"
            val profit = item.netProfit?.let(::formatMoney) ?: "--"
            val revenueYoY = item.revenueYoY?.let { "，同比 ${formatNumber(it)}%" }.orEmpty()
            val profitYoY = item.profitYoY?.let { "，同比 ${formatNumber(it)}%" }.orEmpty()
            val eps = item.basicEps?.let { "，EPS ${formatNumber(it, 4)} 元" }.orEmpty()
            StockNewsEntry(
                publishedAt = date,
                category = StockNewsCategory.FINANCIAL,
                title = "${item.dataType.orEmpty().ifBlank { "最新财报" }}：营收 $revenue$revenueYoY，" +
                    "归母净利润 $profit$profitYoY$eps",
                source = "东方财富财务数据",
                url = "https://data.eastmoney.com/bbsj/$code.html",
            )
        }
    }

    private suspend fun fetchResearchReports(code: String): List<StockNewsEntry> {
        val text = client.get(RESEARCH_URL) {
            parameter("industryCode", "*")
            parameter("pageSize", 10)
            parameter("industry", "*")
            parameter("rating", "*")
            parameter("ratingChange", "*")
            parameter("beginTime", "2000-01-01")
            parameter("endTime", "2099-12-31")
            parameter("pageNo", 1)
            parameter("fields", "")
            parameter("qType", 0)
            parameter("orgCode", "")
            parameter("code", code)
            parameter("rcode", "")
            parameter("p", 1)
            parameter("pageNum", 1)
            parameter("pageNumber", 1)
            header("User-Agent", UA)
            header("Referer", "https://data.eastmoney.com/")
        }.bodyAsText()
        val body = json.decodeFromString<ResearchEnvelope>(text)
        return body.data.orEmpty().mapNotNull { item ->
            val title = item.title.orEmpty().stripHtml().trim()
            val date = item.publishDate.orEmpty()
            if (title.isBlank() || date.isBlank()) return@mapNotNull null
            StockNewsEntry(
                publishedAt = date,
                category = StockNewsCategory.RESEARCH,
                title = item.rating.orEmpty().takeIf { it.isNotBlank() }
                    ?.let { "[$it] $title" } ?: title,
                source = item.orgName.orEmpty().ifBlank { "东方财富研报" },
                url = item.infoCode?.takeIf { it.isNotBlank() }
                    ?.let { "https://pdf.dfcfw.com/pdf/H3_${it}_1.pdf" }
                    .orEmpty(),
            )
        }
    }

    private fun classifyNews(title: String): StockNewsCategory = when {
        title.containsAny("研报", "评级", "目标价", "券商观点", "机构调研") ->
            StockNewsCategory.RESEARCH
        title.containsAny("主力资金", "资金流向", "龙虎榜", "融资融券", "北向资金", "大宗交易") ->
            StockNewsCategory.CAPITAL
        else -> StockNewsCategory.NEWS
    }

    private fun classifyAnnouncement(title: String): StockNewsCategory = when {
        title.containsAny("年度报告", "半年度报告", "季度报告", "业绩预告", "业绩快报", "财务报告") ->
            StockNewsCategory.FINANCIAL
        else -> StockNewsCategory.ANNOUNCEMENT
    }

    private fun unwrapJsonp(text: String): String {
        val trimmed = text.trim()
        if (trimmed.startsWith("{")) return trimmed
        val start = trimmed.indexOf('(')
        val end = trimmed.lastIndexOf(')')
        require(start >= 0 && end > start) { "东方财富返回了无效数据" }
        return trimmed.substring(start + 1, end)
    }

    private fun String.containsAny(vararg values: String): Boolean = values.any(::contains)

    private fun String.stripHtml(): String = replace(HTML_TAG, "")
        .replace("&nbsp;", " ")
        .replace("&amp;", "&")
        .replace("&quot;", "\"")

    private fun String.escapeJson(): String = replace("\\", "\\\\").replace("\"", "\\\"")

    private fun formatMoney(value: Double): String = when {
        kotlin.math.abs(value) >= 100_000_000 -> "${formatNumber(value / 100_000_000)}亿元"
        kotlin.math.abs(value) >= 10_000 -> "${formatNumber(value / 10_000)}万元"
        else -> "${formatNumber(value)}元"
    }

    private fun formatNumber(value: Double, digits: Int = 2): String =
        when (digits) {
            4 -> formatDecimal(value, 4)
            else -> formatDecimal(value)
        }

    @Serializable
    private data class NewsEnvelope(val result: NewsResult? = null)

    @Serializable
    private data class NewsResult(
        @SerialName("cmsArticleWebOld") val articles: List<NewsArticle>? = null,
    )

    @Serializable
    private data class NewsArticle(
        val title: String? = null,
        val date: String? = null,
        val mediaName: String? = null,
        val url: String? = null,
    )

    @Serializable
    private data class AnnouncementEnvelope(val data: AnnouncementData? = null)

    @Serializable
    private data class AnnouncementData(
        @SerialName("list") val items: List<AnnouncementItem>? = null,
    )

    @Serializable
    private data class AnnouncementItem(
        @SerialName("art_code") val artCode: String? = null,
        @SerialName("title_ch") val titleCh: String? = null,
        val title: String? = null,
        @SerialName("display_time") val displayTime: String? = null,
        @SerialName("notice_date") val noticeDate: String? = null,
    )

    @Serializable
    private data class FundFlowEnvelope(val data: FundFlowData? = null)

    @Serializable
    private data class FundFlowData(val klines: List<String>? = null)

    @Serializable
    private data class FinancialEnvelope(val result: FinancialResult? = null)

    @Serializable
    private data class FinancialResult(val data: List<FinancialItem>? = null)

    @Serializable
    private data class FinancialItem(
        @SerialName("REPORTDATE") val reportDate: String? = null,
        @SerialName("NOTICE_DATE") val noticeDate: String? = null,
        @SerialName("DATATYPE") val dataType: String? = null,
        @SerialName("BASIC_EPS") val basicEps: Double? = null,
        @SerialName("TOTAL_OPERATE_INCOME") val revenue: Double? = null,
        @SerialName("PARENT_NETPROFIT") val netProfit: Double? = null,
        @SerialName("YSTZ") val revenueYoY: Double? = null,
        @SerialName("SJLTZ") val profitYoY: Double? = null,
    )

    @Serializable
    private data class ResearchEnvelope(val data: List<ResearchItem>? = null)

    @Serializable
    private data class ResearchItem(
        val title: String? = null,
        @SerialName("publishDate") val publishDate: String? = null,
        @SerialName("orgSName") val orgName: String? = null,
        @SerialName("emRatingName") val rating: String? = null,
        @SerialName("infoCode") val infoCode: String? = null,
    )

    companion object {
        private const val NEWS_URL = "https://search-api-web.eastmoney.com/search/jsonp"
        private const val ANNOUNCEMENT_URL =
            "https://np-anotice-stock.eastmoney.com/api/security/ann"
        private const val FUND_FLOW_URL =
            "https://push2his.eastmoney.com/api/qt/stock/fflow/daykline/get"
        private const val FINANCIAL_URL =
            "https://datacenter-web.eastmoney.com/api/data/v1/get"
        private const val RESEARCH_URL =
            "https://reportapi.eastmoney.com/report/list"
        private const val UA =
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36"
        private val HTML_TAG = Regex("<[^>]+>")
    }
}
